/**
 * Navigation Onboarding
 *
 * Smart defaults and onboarding flows for first-time users.
 * Provides role-based default pins and onboarding step definitions.
 */

/**
 * Get default pins for a user based on role
 * These are the routes most relevant to each role type
 *
 * @param {string} userRole - User's role from RolePermissions
 * @returns {Array} Array of route IDs to pin by default
 */
export const getDefaultPins = (userRole) => {
  const defaults = {
    admin_user: [
      'gtm-stats',
      'board-stats',
      'business-digest',
      'scenarios',
    ],
    sales_ops_user: [
      'forecast',
      'pclose',
      'scenarios',
      'gtm-stats',
    ],
    marketing_ops_user: [
      'pipelineflows',
      'program-effectiveness',
      'gtm-stats',
    ],
    finance_user: [
      'board-stats',
      'customer-invoices',
      'arr-schedule',
      'business-digest',
    ],
    general_user: [
      'business-digest',
      'gtm-stats',
      'forecast',
    ],
    sales_user: [
      'forecast',
    ],
    marketing_user: [
      'pipelineflows',
      'program-effectiveness',
      'gtm-stats',
    ],
  };

  return defaults[userRole] || ['business-digest', 'recipe-designer'];
};

/**
 * Get welcome message for a user based on role
 *
 * @param {string} userRole - User's role
 * @returns {string} Welcome message
 */
export const getWelcomeMessage = (userRole) => {
  const messages = {
    admin_user: 'Welcome! Your most important metrics and tools are pinned for quick access.',
    sales_ops_user: 'Welcome! Start with Forecast and Scenarios to build your pipeline plan.',
    marketing_ops_user: 'Welcome! Explore attribution flows and program effectiveness.',
    finance_user: 'Welcome! Your financial tracking and reporting tools are ready.',
    general_user: 'Welcome! Start with Business Digest for a quick overview.',
    sales_user: 'Welcome! Access your forecast and pipeline tools.',
    marketing_user: 'Welcome! Discover your marketing attribution and effectiveness tools.',
  };

  return messages[userRole] || 'Welcome to your personalized navigation!';
};

/**
 * Initialize first-time user preferences
 * Sets up default pins and navigation preferences
 *
 * @param {string} userId - User ID
 * @param {string} userRole - User's role
 * @param {Function} updateUserPreferences - Function to update user preferences
 * @returns {Promise<Object>} Updated preferences
 */
export const initializeUserPreferences = async (userId, userRole, updateUserPreferences) => {
  const defaultPins = getDefaultPins(userRole);

  const preferences = {
    pinnedRoutes: defaultPins,
    navigationPreferences: {
      onboardingComplete: false,
      onboardingStep: 0,
      collapsedCategories: [],
      discoveryPanelExpanded: false,
    },
    recentRoutes: [],
  };

  if (updateUserPreferences) {
    return await updateUserPreferences(preferences);
  }

  return preferences;
};

/**
 * Onboarding steps for new users
 * Interactive tour of navigation features
 */
export const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    target: null, // No specific target, just a welcome message
    title: 'Welcome to Your New Navigation',
    content: 'We\'ve redesigned the navigation to help you work more efficiently. Let\'s take a quick tour.',
    placement: 'center',
    showSkip: true,
  },
  {
    id: 'hero-section',
    target: '.hero-nav-section',
    title: 'Recipe Designer & Results',
    content: 'Access Recipe Designer and Recipe Results here - your primary workflow tools for building and running automated analyses.',
    placement: 'right',
    highlightElement: true,
  },
  {
    id: 'search',
    target: '.navigation-search',
    title: 'Quick Search',
    content: 'Press Cmd+K or click here to search for any route by name or description. Try it now!',
    placement: 'bottom',
    highlightElement: true,
    requireInteraction: false,
  },
  {
    id: 'pinned-routes',
    target: '.pinned-routes',
    title: 'Pin Your Favorites',
    content: 'Pin routes you use frequently for quick access. We\'ve started you off with some suggestions based on your role.',
    placement: 'right',
    highlightElement: true,
  },
  {
    id: 'suggestions',
    target: '.contextual-suggestions',
    title: 'Smart Suggestions',
    content: 'Based on what you\'re working on, we\'ll suggest relevant routes. These update automatically as your context changes.',
    placement: 'right',
    highlightElement: true,
  },
  {
    id: 'explore-more',
    target: '.discoverable-routes',
    title: 'Explore More',
    content: 'Click here to see all available routes, organized by category. You can filter and pin routes from here too.',
    placement: 'right',
    highlightElement: true,
  },
  {
    id: 'complete',
    target: null,
    title: 'You\'re All Set!',
    content: 'You can customize your navigation anytime by pinning/unpinning routes. Happy analyzing!',
    placement: 'center',
    showSkip: false,
  },
];

/**
 * Get next onboarding step
 *
 * @param {number} currentStep - Current step index
 * @returns {Object|null} Next step or null if complete
 */
export const getNextOnboardingStep = (currentStep) => {
  if (currentStep >= ONBOARDING_STEPS.length - 1) {
    return null;
  }
  return ONBOARDING_STEPS[currentStep + 1];
};

/**
 * Get previous onboarding step
 *
 * @param {number} currentStep - Current step index
 * @returns {Object|null} Previous step or null if at start
 */
export const getPreviousOnboardingStep = (currentStep) => {
  if (currentStep <= 0) {
    return null;
  }
  return ONBOARDING_STEPS[currentStep - 1];
};

/**
 * Check if user should see onboarding
 *
 * @param {Object} userPreferences - User's navigation preferences
 * @returns {boolean} Whether to show onboarding
 */
export const shouldShowOnboarding = (userPreferences) => {
  // Show onboarding if:
  // 1. User has never completed it
  // 2. User has no pinned routes (might be a new feature rollout)
  const hasCompletedOnboarding = userPreferences?.navigationPreferences?.onboardingComplete;
  const hasPins = userPreferences?.pinnedRoutes?.length > 0;

  return !hasCompletedOnboarding || !hasPins;
};

/**
 * Mark onboarding as complete
 *
 * @param {Function} updatePreferences - Function to update preferences
 * @returns {Promise<void>}
 */
export const completeOnboarding = async (updatePreferences) => {
  if (updatePreferences) {
    await updatePreferences({
      onboardingComplete: true,
      onboardingStep: ONBOARDING_STEPS.length,
    });
  }
};

/**
 * Get helpful tips for each route category
 * Used in empty states and guidance
 */
export const CATEGORY_TIPS = {
  'Core': {
    tip: 'Recipe Designer and Results are your workflow hub - start here to build automated analyses.',
    learnMoreUrl: '/docs/recipe-designer',
  },
  'Predict': {
    tip: 'Forecast your revenue and model different scenarios to plan your business.',
    learnMoreUrl: '/docs/forecasting',
  },
  'Stats': {
    tip: 'Track your key metrics and KPIs with executive-level dashboards.',
    learnMoreUrl: '/docs/analytics',
  },
  'Sales Drivers': {
    tip: 'Understand what drives your sales performance and where to improve.',
    learnMoreUrl: '/docs/sales-analytics',
  },
  'Marketing Drivers': {
    tip: 'Measure marketing effectiveness and multi-touch attribution.',
    learnMoreUrl: '/docs/marketing-analytics',
  },
  'Workflows': {
    tip: 'Manage your data, configurations, and operational workflows.',
    learnMoreUrl: '/docs/workflows',
  },
  'Setup': {
    tip: 'Configure forecasting, quotas, and metadata before running analysis.',
    learnMoreUrl: '/docs/configuration',
  },
};

/**
 * Get tip for a category
 *
 * @param {string} category - Category name
 * @returns {Object} Tip object with message and learn more link
 */
export const getCategoryTip = (category) => {
  return CATEGORY_TIPS[category] || {
    tip: 'Explore routes in this category to discover new insights.',
    learnMoreUrl: '/docs',
  };
};

/**
 * Feature announcement for navigation redesign
 * Shown to existing users on first load with new navigation
 */
export const FEATURE_ANNOUNCEMENT = {
  title: 'Navigation Redesign',
  message: 'We\'ve redesigned the navigation to make it easier to find what you need. Pin your favorites, use quick search, and get smart suggestions based on what you\'re working on.',
  actions: [
    {
      label: 'Take Tour',
      action: 'start-onboarding',
    },
    {
      label: 'Dismiss',
      action: 'dismiss',
    },
  ],
  showOnce: true,
};

export default {
  getDefaultPins,
  getWelcomeMessage,
  initializeUserPreferences,
  ONBOARDING_STEPS,
  getNextOnboardingStep,
  getPreviousOnboardingStep,
  shouldShowOnboarding,
  completeOnboarding,
  CATEGORY_TIPS,
  getCategoryTip,
  FEATURE_ANNOUNCEMENT,
};
